import React, { useEffect, useState } from 'react';
import { Typography, theme, Spin, Alert, Badge } from 'antd';
import {
  IconLayoutDashboard,
  IconUsers,
  IconHomeStar,
  IconUserCog,
  IconBriefcase,
  IconMapPin,
  IconCalendarEvent,
  IconStar,
  IconFlag,
  IconPhoto,
  IconWorldOff,
  IconId,
  IconShieldCog,
  IconHistory,
  IconFileExport,
  IconSpeakerphone,
  IconCreditCard,
  IconMessage,
  IconPlugConnectedX,
  IconToggleLeft,
  IconAlertTriangle,
  IconScale,
  IconMailForward,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import AppLayout from '../../components/Layouts/Layout';
import SidebarContentLayout from '../../components/Layouts/SidebarContentLayout';
import SidebarNavList from '../../components/Layouts/SidebarNavList';
import { useAuth } from '../../contextProviders/AuthProvider';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { getAdminSession, getAdminConversationsUnreadCount } from '../../services/AdminService';
import DashboardSection from './sections/DashboardSection';
import UsersSection from './sections/UsersSection';
import HostsSection from './sections/HostsSection';
import OperatorsSection from './sections/OperatorsSection';
import CatalogSection from './sections/CatalogSection';
import SpacesSection from './sections/SpacesSection';
import BookingsSection from './sections/BookingsSection';
import ReviewsSection from './sections/ReviewsSection';
import ReportsSection from './sections/ReportsSection';
import PhotoModerationSection from './sections/PhotoModerationSection';
import GeoBlockingSection from './sections/GeoBlockingSection';
import AgeVerificationSection from './sections/AgeVerificationSection';
import RolesSection from './sections/RolesSection';
import AuditLogSection from './sections/AuditLogSection';
import GdprRequestsSection from './sections/GdprRequestsSection';
import AnnouncementsSection from './sections/AnnouncementsSection';
import FinanceSection from './sections/FinanceSection';
import MessagesSection from './sections/MessagesSection';
import ErpSyncSection from './sections/ErpSyncSection';
import FeatureFlagsSection from './sections/FeatureFlagsSection';
import SignupLeadsSection from './sections/SignupLeadsSection';
import BookingLineResolutionSection from './sections/BookingLineResolutionSection';
import DisputesSection from './sections/DisputesSection';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

const { Title } = Typography;

type AdminSectionKey =
  | 'dashboard'
  | 'users'
  | 'hosts'
  | 'operators'
  | 'catalog'
  | 'spaces'
  | 'bookings'
  | 'reviews'
  | 'reports'
  | 'photo-moderation'
  | 'geo-blocking'
  | 'age-verification'
  | 'roles'
  | 'audit-log'
  | 'gdpr'
  | 'announcements'
  | 'finance'
  | 'messages'
  | 'erp-sync'
  | 'feature-flags'
  | 'signup-leads'
  | 'booking-lines'
  | 'disputes';

const SECTION_KEYS: AdminSectionKey[] = [
  'dashboard',
  'users',
  'hosts',
  'operators',
  'catalog',
  'spaces',
  'bookings',
  'reviews',
  'reports',
  'photo-moderation',
  'geo-blocking',
  'age-verification',
  'roles',
  'audit-log',
  'gdpr',
  'announcements',
  'finance',
  'messages',
  'erp-sync',
  'feature-flags',
  'signup-leads',
  'booking-lines',
  'disputes',
];

const isSectionKey = (value: string | null): value is AdminSectionKey =>
  !!value && (SECTION_KEYS as string[]).includes(value);

// "Roles y permisos" grants/revokes Admin and Moderator itself -- a
// Moderator must never see it, let alone use it (see AdminRoles.Admin-only
// guard the backend will use once this section's endpoints exist).
// "Feature flags" is Admin-only too, matching AdminFeatureFlagsController's
// own [Authorize(Roles = AdminRoles.Admin)] guard.
// "Lista de espera" holds e-mail addresses strangers left before the
// platform opened -- personal data with no account behind it, and the same
// Admin-only guard as AdminSignupLeadsController's own.
const ADMIN_ONLY_SECTIONS: AdminSectionKey[] = ['roles', 'feature-flags', 'signup-leads'];

const SECTIONS: { key: AdminSectionKey; icon: React.ReactNode; labelKey: string }[] = [
  { key: 'dashboard', icon: <IconLayoutDashboard size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_DASHBOARD },
  { key: 'users', icon: <IconUsers size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_USERS },
  { key: 'hosts', icon: <IconHomeStar size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_HOSTS },
  { key: 'operators', icon: <IconUserCog size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_OPERATORS },
  { key: 'catalog', icon: <IconBriefcase size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_CATALOG },
  { key: 'spaces', icon: <IconMapPin size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_SPACES },
  { key: 'bookings', icon: <IconCalendarEvent size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_BOOKINGS },
  { key: 'reviews', icon: <IconStar size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_REVIEWS },
  { key: 'reports', icon: <IconFlag size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_REPORTS },
  { key: 'photo-moderation', icon: <IconPhoto size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_PHOTO_MODERATION },
  { key: 'geo-blocking', icon: <IconWorldOff size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_GEO_BLOCKING },
  { key: 'age-verification', icon: <IconId size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_AGE_VERIFICATION },
  { key: 'roles', icon: <IconShieldCog size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_ROLES },
  { key: 'audit-log', icon: <IconHistory size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_AUDIT_LOG },
  { key: 'gdpr', icon: <IconFileExport size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_GDPR },
  { key: 'announcements', icon: <IconSpeakerphone size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_ANNOUNCEMENTS },
  { key: 'finance', icon: <IconCreditCard size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_FINANCE },
  { key: 'messages', icon: <IconMessage size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_MESSAGES },
  { key: 'erp-sync', icon: <IconPlugConnectedX size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_ERP_SYNC },
  { key: 'feature-flags', icon: <IconToggleLeft size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_FEATURE_FLAGS },
  { key: 'signup-leads', icon: <IconMailForward size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_SIGNUP_LEADS },
  { key: 'booking-lines', icon: <IconAlertTriangle size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_BOOKING_LINES },
  { key: 'disputes', icon: <IconScale size={18} />, labelKey: TRANSLATIONS.ADMIN_NAV_DISPUTES },
];

const SECTION_COMPONENTS: Record<AdminSectionKey, React.ReactNode> = {
  dashboard: <DashboardSection />,
  users: <UsersSection />,
  hosts: <HostsSection />,
  operators: <OperatorsSection />,
  catalog: <CatalogSection />,
  spaces: <SpacesSection />,
  bookings: <BookingsSection />,
  reviews: <ReviewsSection />,
  reports: <ReportsSection />,
  'photo-moderation': <PhotoModerationSection />,
  'geo-blocking': <GeoBlockingSection />,
  'age-verification': <AgeVerificationSection />,
  roles: <RolesSection />,
  'audit-log': <AuditLogSection />,
  gdpr: <GdprRequestsSection />,
  announcements: <AnnouncementsSection />,
  finance: <FinanceSection />,
  messages: <MessagesSection />,
  'erp-sync': <ErpSyncSection />,
  'feature-flags': <FeatureFlagsSection />,
  'signup-leads': <SignupLeadsSection />,
  'booking-lines': <BookingLineResolutionSection />,
  disputes: <DisputesSection />,
};

/**
 * Admin panel shell -- same two-column layout as Configuración de cuenta
 * (see Settings.tsx): sidebar left, section content right, section kept in
 * the URL (?section=) the same way. Content sections are placeholders for
 * now (see AdminComingSoon) -- this first pass only wires up the navigation
 * structure and the role guard, per the panel's own build order (roles +
 * layout first, real section content later).
 *
 * Access is checked twice: the frontend route itself only renders for a
 * signed-in Admin/Moderator (see ProtectedRoute's requireAdminOrModerator,
 * used on the /admin route in AppRoutes), and this component additionally
 * confirms it server-side via GET /api/heavy-duty-machinery/admin/session (AdminController)
 * before showing any content -- every real admin endpoint built on top of
 * this panel enforces the same [Authorize(Roles = ...)] guard independently,
 * so hiding the sidebar entry is never the only thing standing between a
 * regular user and this data.
 */
const Admin: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { isAdmin } = useAuth();
  const { isMobile } = useAppBreakpoint();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessionChecked, setSessionChecked] = useState(false);
  const [sessionError, setSessionError] = useState(false);
  // The only "someone needs a reply" signal for Mensajes admins get without
  // already having that section open (see AdminConversationsController.
  // GetUnreadCount's own doc comment) -- refetched on every section change
  // rather than just once on mount, so it stays reasonably current as an
  // admin reads through conversations and moves on to something else.
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

  const rawSection = searchParams.get('section');
  const requestedSection: AdminSectionKey = isSectionKey(rawSection) ? rawSection : 'dashboard';
  // A Moderator typing ?section=roles directly must land on Dashboard, same
  // as if that sidebar entry didn't exist -- it's not just hidden, it's
  // actually off-limits (see ADMIN_ONLY_SECTIONS).
  const activeSection: AdminSectionKey =
    ADMIN_ONLY_SECTIONS.includes(requestedSection) && !isAdmin ? 'dashboard' : requestedSection;

  useEffect(() => {
    getAdminSession()
      .then(() => setSessionError(false))
      .catch(() => setSessionError(true))
      .finally(() => setSessionChecked(true));
  }, []);

  useEffect(() => {
    if (!sessionChecked || sessionError) return;
    getAdminConversationsUnreadCount()
      .then(setUnreadMessagesCount)
      .catch(() => undefined);
  }, [activeSection, sessionChecked, sessionError]);

  const visibleSections = SECTIONS.filter((section) => isAdmin || !ADMIN_ONLY_SECTIONS.includes(section.key));

  if (!sessionChecked) {
    return (
      <AppLayout>
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </AppLayout>
    );
  }

  if (sessionError) {
    return (
      <AppLayout>
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_SESSION_ERROR)} />
      </AppLayout>
    );
  }

  const sidebarNav = (
    <SidebarNavList
      activeKey={activeSection}
      onSelect={(key) => setSearchParams({ section: key })}
      pillLayoutId="admin-nav-pill"
      items={visibleSections.map((section) => ({
        key: section.key,
        icon: section.icon,
        label: translate(t, section.labelKey),
        trailing:
          section.key === 'messages' && unreadMessagesCount > 0 ? (
            <Badge count={unreadMessagesCount} size="small" />
          ) : undefined,
      }))}
    />
  );

  return (
    <AppLayout>
      <div
        style={{
          width: '100%',
          maxWidth: 1300,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: 16,
          overflow: 'hidden',
          minHeight: 560,
          background: token.colorBgContainer,
        }}
      >
        <SidebarContentLayout
          title={
            <Title level={4} style={{ marginBottom: 20 }}>
              {translate(t, TRANSLATIONS.ADMIN_PAGE_TITLE)}
            </Title>
          }
          sidebar={sidebarNav}
          activeKey={activeSection}
          sidebarStyle={{ borderRight: `1px solid ${token.colorBorderSecondary}`, padding: '28px 20px' }}
          // 32px of side padding costs a phone a fifth of its width; the
          // desktop figure is left exactly as it was.
          contentStyle={{ padding: isMobile ? '20px 16px' : '28px 32px', overflowY: 'auto' }}
        >
          {SECTION_COMPONENTS[activeSection]}
        </SidebarContentLayout>
      </div>
    </AppLayout>
  );
};

export default Admin;
